import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnnouncementBar } from "../components/site/AnnouncementBar";
import { Header } from "../components/site/Header";
import { Footer } from "../components/site/Footer";
import { GrainOverlay } from "../components/site/GrainOverlay";
import { ContactSection } from "../components/site/ContactSection";

const scrollToContact = () => {
  const el = document.getElementById("contact");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const InnerCTA = ({ heading, copy, cta, testid = "inner-cta" }) => (
  <section
    data-testid={testid}
    className="relative bg-[#0A0A0A] border-y border-white/10 py-24 md:py-32"
  >
    <div className="max-w-5xl mx-auto px-4 md:px-6 text-center">
      <h2 className="font-headings text-4xl md:text-6xl lg:text-7xl font-black tracking-tight leading-[1.02] text-white">
        {heading}
      </h2>
      {copy && (
        <p className="mt-6 text-base md:text-lg text-zinc-400 max-w-2xl mx-auto leading-relaxed">
          {copy}
        </p>
      )}
      <button
        onClick={scrollToContact}
        data-testid={`${testid}-btn`}
        className="mt-10 inline-flex items-center gap-2 bg-[#FF5F1F] hover:bg-[#E04F15] text-black font-bold px-8 py-4 text-sm md:text-base transition-all"
      >
        {cta} →
      </button>
    </div>
  </section>
);

export const InnerPageHero = ({ eyebrow, h1, intro, primaryCta = "Start a Conversation", secondaryCta, breadcrumbs = [] }) => (
  <section
    id="top"
    data-testid="page-hero"
    className="relative overflow-hidden bg-[#050505] pt-16 md:pt-24 pb-16 md:pb-24"
  >
    <div className="absolute inset-0 bg-grid opacity-40" aria-hidden="true" />
    <div className="absolute inset-0 radial-spotlight" aria-hidden="true" />
    <div
      className="absolute -top-40 -right-20 w-[500px] h-[500px] rounded-full blur-[140px] opacity-25"
      style={{ background: "#FF5F1F" }}
      aria-hidden="true"
    />

    <div className="relative max-w-7xl mx-auto px-4 md:px-6">
      {breadcrumbs.length > 0 && (
        <div className="flex items-center gap-2 font-mono text-[10px] md:text-xs uppercase tracking-[0.2em] text-zinc-500 mb-8">
          {breadcrumbs.map((b, i) => (
            <React.Fragment key={i}>
              {b.to ? (
                <Link to={b.to} className="hover:text-[#FF5F1F] transition-colors">
                  {b.label}
                </Link>
              ) : (
                <span>{b.label}</span>
              )}
              {i < breadcrumbs.length - 1 && <span className="text-zinc-700">/</span>}
            </React.Fragment>
          ))}
        </div>
      )}

      <div className="flex items-center gap-3 mb-8">
        <div className="h-px w-8 bg-[#FF5F1F]" />
        <span className="font-mono text-[10px] md:text-xs uppercase tracking-[0.25em] text-[#FF5F1F]">
          {eyebrow}
        </span>
      </div>

      <h1
        data-testid="page-h1"
        className="font-headings text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black tracking-[-0.02em] leading-[1.02] text-white max-w-5xl"
      >
        {h1}
      </h1>

      <p className="mt-8 md:mt-10 max-w-3xl text-base md:text-lg text-zinc-400 leading-relaxed">
        {intro}
      </p>

      <div className="mt-10 flex flex-col sm:flex-row gap-4">
        <button
          onClick={scrollToContact}
          data-testid="hero-primary-cta"
          className="inline-flex items-center gap-2 bg-[#FF5F1F] hover:bg-[#E04F15] text-black font-bold px-7 py-4 text-sm md:text-base transition-all"
        >
          {primaryCta} →
        </button>
        {secondaryCta && (
          <Link
            to="/work"
            data-testid="hero-secondary-cta"
            className="inline-flex items-center gap-2 border border-white/15 bg-white/[0.02] hover:bg-white/[0.06] text-white font-medium px-7 py-4 text-sm md:text-base transition-colors"
          >
            {secondaryCta} →
          </Link>
        )}
      </div>
    </div>
  </section>
);

export const InnerPageLayout = ({ children, title }) => {
  const location = useLocation();

  useEffect(() => {
    if (title) document.title = `${title} — Techovarya`;
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname, title]);

  return (
    <div className="App min-h-screen bg-[#050505] text-white antialiased selection:bg-[#FF5F1F] selection:text-black">
      <GrainOverlay />
      <AnnouncementBar />
      <Header />
      <main>{children}</main>
      <ContactSection />
      <Footer />
    </div>
  );
};
